
import { Directive, Input, TemplateRef, ViewContainerRef, EmbeddedViewRef, Renderer2 } from '@angular/core';
import { Observable, of, Subject } from 'rxjs';
import { switchMap, map } from 'rxjs/operators';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { Platform } from '@angular/cdk/platform';
import { UsersService } from '@shared/services/users/users.service';
import { UserRolesEnum } from '@collections/users/enums/user-roles.enums';
import { BreakpointObserver, BreakpointState } from '@angular/cdk/layout';
import { Breakpoints } from '@fuse/services/match-media.service';

export const ExtBreakpoints = {
  ...Breakpoints,
  'lt-sm': '(max-width: 639px)',
  'lt-md': '(max-width: 767px)',
  'lt-lg': '(max-width: 1023px)',
  'lt-xl': '(max-width: 1279px)',
  'lt-2xl': '(max-width: 1589px)',
  'gt-xs': '(min-width: 480px)',
  'gt-sm': '(min-width: 640px)',
  'gt-md': '(min-width: 768px)',
  'gt-lg': '(min-width: 1024px)',
  'gt-xl': '(min-width: 1280px)'
};

export type MediaSelector = 'sm' | 'md' | 'lg' | 'xl' | '2xl'
| 'lt-sm' | 'lt-md' | 'lt-lg' | 'lt-xl' | 'lt-2xl'
| 'gt-xs'| 'gt-sm' | 'gt-md' | 'gt-lg' | 'gt-xl' ;

export type RenderIfConditions = {
    mediaSelector?: MediaSelector,
    roles?: UserRolesEnum[],
    baseCondition?: boolean,
    logicalMode?: 'AND' | 'OR',
    // flex block inline-flex ecc.
    // https://tailwindcss.com/docs/display
    // (serve lato server per visualizzare un elemento da uno specifico breakpoint in poi)
    displayClass?: string;
  };

@UntilDestroy()
@Directive({
    selector: '[renderIf]',
    standalone: true
})
export class RenderIfDirective {

  private viewRef: EmbeddedViewRef<any> = undefined;
  private renderIfmatcher = new Subject<RenderIfConditions>();
  private lastValue: RenderIfConditions;

  // fa si che vengano incluse queste classi (!important)
  private tailwind_hidden_class = `
  sm:!hidden
  md:!hidden
  lg:!hidden
  xl:!hidden
  2xl:!hidden
  `;
    /**
     *
     * @param {RenderIfConditions} value
     */
  @Input()
  set renderIf(value: RenderIfConditions) {
    const baseCondition = value.baseCondition;
    value.baseCondition = baseCondition === undefined ? true : baseCondition;
    this.lastValue = value;
    this.renderIfmatcher.next(value);
  }

  // https://angular.io/guide/structural-directives#directive-type-checks
  // dovrebbe effettuare un type check sull'input, ma sembra non funzionare
  static ngTemplateGuard_renderIf(dir: RenderIfDirective, expr: RenderIfConditions): expr is RenderIfConditions {
    return true;
  }

  constructor(
    private platform: Platform,
    private viewContainer: ViewContainerRef,
    private templateRef: TemplateRef<any>,
    private usersService: UsersService,
    private renderer: Renderer2,
    private observer: BreakpointObserver
  ) {
    if (this.platform.isBrowser) {
      this.watchMedia();
      this.usersService.onUserChange
      .pipe(
        untilDestroyed(this)
      ).subscribe( () => {
        if (this.lastValue) {
          this.renderIfmatcher.next(this.lastValue);
        }
      });
    } else {

      this.renderIfmatcher.pipe(
        untilDestroyed(this)
      ).subscribe(val => {
        // lato server, effettua il render se non è prevista come condizione essenziale
        // che l'utente abbia un ruolo determinato.
        if ((val.logicalMode && val.logicalMode !== 'AND') || !val.roles) {
          this.updateView(true, val.mediaSelector, val.displayClass);
        }
      });
    }

  }

  /**
   * Watch for mediaChange(s) on new mediaQuery
   */
  private watchMedia(): void {
    const updateView = this.updateView.bind(this);
    const getQuery = (selector) => {
      return ExtBreakpoints[selector];
    };

    this.renderIfmatcher.pipe(
      switchMap(v => {
        // se baseCondition è impostata a false non effettua mai il render
        if (!v.baseCondition) {
          return of(false);
        }
        const query = getQuery(v.mediaSelector);
        return query ? this.observer.observe(query)
          .pipe(
            map((state: BreakpointState) => {

              if (v.logicalMode === 'AND' || !v.roles) {
                return state.matches && this.usersService.isInRole(v.roles);
              } else {
                return state.matches || this.usersService.isInRole(v.roles);
              }
            })
          ) : new Observable<boolean>( observer => {
            observer.next(this.usersService.isInRole(v.roles));
          });
      }),
      untilDestroyed(this)
    )
    .subscribe(updateView);
  }

  /**
   * Create or clear view instance
   */
  private updateView(isActive: boolean, mediaSelector?: string, displayClass?: string): void {
    if (isActive && !this.viewRef) {
      this.viewRef = this.viewContainer.createEmbeddedView(this.templateRef);
      if ( mediaSelector) { // eseguito solo lato server, per aggiungere classe
        const el = this.viewRef.rootNodes[0];
        if (el instanceof HTMLElement) {

          // Se il media selector è di tipo lower than, applica la classe hidden a quel punto
          const bp = mediaSelector.replace('lt-', '').replace('gt-', '');
          if (mediaSelector.startsWith('lt-')) {
            this.renderer.addClass(el, `${bp}:!hidden`);
          } else if (mediaSelector.startsWith('gt-')) {

            // tutti gli altri selettori (sm md lg xl 2xl)
            // andrebbero inserite tutte le possibili combinazioni tra selettore e display
            // come per tailwind_hidden_class, ma forse non ne vale la pena. vediamo in futuro.
            // this.renderer.addClass(el, `!hidden`);
            // displayClass = displayClass ?? 'block';
            // this.renderer.addClass(el, `${bp}:!${displayClass}`);
          }
        }
      }
    } else if (!isActive && this.viewRef) {
      this.viewContainer.clear();
      this.viewRef = undefined;
    }
  }

}
